import React from "react";
import { SortType } from "../model";

const COMPONENT_ID = 'type-badge';

type TypeBadgeProps = {
    id: string;
    type: SortType;
};

/**
 * Tone tint + tone ink — the exact pairs DS `Tag`/`Status` paint their tone chips
 * with, so this pill follows the dark ladder in `tokens.css` (the old
 * `bg-*-100 / border-*-300 / text-*-800` palette steps could never theme). The
 * borders are gone with them: DS tone chips carry no border, the tint IS the
 * boundary. Mapping keeps the old hue distinctions: SYSTEM amber → warning,
 * TEXT blue → info, INTEGER/DECIMAL purple → accent (`tone-accent-tint` is the
 * purple family), everything else → neutral.
 *
 * Still a bare span rather than DS `Tag`: this badge stretches `tw-w-full` to
 * fill its table cell, which `Tag` (a content-width chip) does not do.
 */
const getBadgeConfig = (type: SortType) => {
    switch (type) {
        case 'SYSTEM':
            return {
                displayText: 'System',
                className: 'tw-bg-tone-warning-tint tw-text-tone-warning-ink',
            };
        case 'TEXT':
            return {
                displayText: 'Text',
                className: 'tw-bg-tone-info-tint tw-text-tone-info-ink',
            };
        case 'INTEGER':
            return {
                displayText: 'Integer',
                className: 'tw-bg-tone-accent-tint tw-text-tone-accent-ink',
            };
        case 'DECIMAL':
            return {
                displayText: 'Decimal',
                className: 'tw-bg-tone-accent-tint tw-text-tone-accent-ink',
            };
        default:
            return {
                displayText: type,
                className: 'tw-bg-tone-neutral-tint tw-text-tone-neutral-ink',
            };
    }
};

export const TypeBadge = ({ id, type }: TypeBadgeProps) => {
    const { displayText, className } = getBadgeConfig(type);

    return (
        <span
            id={`${COMPONENT_ID}-${id}`}
            data-testid={`${COMPONENT_ID}-${id}`}
            className={`tw-inline-flex tw-w-full tw-items-center tw-justify-center tw-px-3 tw-py-1 tw-text-sm tw-font-medium tw-rounded ${className}`}
        >
            {displayText}
        </span>
    );
};